// Client-side register export: the rows a page already holds, as a CSV a spreadsheet opens.
//
// Data exports stay CSV / XLSX (docs/DOCUMENT-TEMPLATES.md). Every cell is guarded against
// formula injection: a value starting with = + - @ (or a tab / carriage return) is prefixed with
// an apostrophe, so a supplier called "=HYPERLINK(...)" is text in Excel, never a formula.

export interface ExportColumn<T> {
  header: string;
  value: (row: T) => string | number | boolean | Date | null | undefined;
}

function cell(v: unknown): string {
  if (v === null || v === undefined) return "";
  let s = v instanceof Date ? v.toISOString().slice(0, 10) : typeof v === "boolean" ? (v ? "Yes" : "No") : String(v);
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function toCsv<T>(columns: ExportColumn<T>[], rows: T[]): string {
  const lines = [columns.map((c) => cell(c.header)).join(",")];
  for (const r of rows) lines.push(columns.map((c) => cell(c.value(r))).join(","));
  // BOM so Excel reads UTF-8 (names with accents, £) correctly.
  return "﻿" + lines.join("\r\n");
}

export function downloadCsv<T>(name: string, columns: ExportColumn<T>[], rows: T[]): void {
  const blob = new Blob([toCsv(columns, rows)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${name}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/** Case-insensitive match of a query against any of the given fields. */
export function matchesSearch(query: string, ...fields: Array<string | null | undefined>): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return fields.some((f) => (f ?? "").toLowerCase().includes(q));
}

export interface SortOption<T> {
  key: string;
  label: string;
  compare: (a: T, b: T) => number;
}

/** Comparators for the usual column types; nulls always sort last. */
export const by = {
  text: <T,>(get: (r: T) => string | null | undefined) => (a: T, b: T) => {
    const x = get(a), y = get(b);
    if (!x) return y ? 1 : 0;
    if (!y) return -1;
    return x.localeCompare(y);
  },
  date: <T,>(get: (r: T) => string | Date | null | undefined, dir: "asc" | "desc" = "asc") => (a: T, b: T) => {
    const x = get(a), y = get(b);
    if (!x) return y ? 1 : 0;
    if (!y) return -1;
    const d = new Date(x).getTime() - new Date(y).getTime();
    return dir === "asc" ? d : -d;
  },
  rank: <T,>(get: (r: T) => string | null | undefined, order: readonly string[]) => (a: T, b: T) => {
    const x = order.indexOf(get(a) ?? ""), y = order.indexOf(get(b) ?? "");
    return (x < 0 ? 99 : x) - (y < 0 ? 99 : y);
  },
  number: <T,>(get: (r: T) => number | null | undefined, dir: "asc" | "desc" = "desc") => (a: T, b: T) => {
    const x = get(a), y = get(b);
    if (x == null) return y == null ? 0 : 1;
    if (y == null) return -1;
    return dir === "asc" ? x - y : y - x;
  },
};

/** Severity / criticality order used across the registers, most serious first. */
export const SEVERITY_ORDER = ["critical", "high", "medium", "low"] as const;
